import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
// import { apiEndpoints, handleApiError } from '../../services/api';
import { toast } from 'react-hot-toast';
import { createApiUrl, createApiUrlWithPrefix, getUserImageUrl } from '../../config/api';
import { useUserCounts } from '../../contexts/UserCountsContext';
import {
  Search,
  UserPlus,
  Edit,
  Trash2,
  RefreshCw,
  Eye,
  ChevronLeft,
  ChevronRight,
  // QrCode,
  CreditCard,
  Printer,
  Shield,
  Calendar,
  BookOpen,
  MoreVertical
} from 'lucide-react';
import UserCreationModal from '../UserCreationModal';
import UserEditModal from '../UserEditModal';
import UserDetailsModal from '../UserDetailsModal';
import UserBadgeModal from '../UserBadgeModal';

import { API_BASE_URL } from '../../config/api';
function Riders() {
  const { t, i18n } = useTranslation();
  const { refreshUserCounts } = useUserCounts();
  const isRTL = i18n.language === 'ar';

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusUser, setStatusUser] = useState(null);
  const [riderStatus, setRiderStatus] = useState({
    insurance: null,
    forfait: null,
    forfaits: [],
    loading: false
  });
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [bonusUser, setBonusUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null); // Track which dropdown is open
  const [bonusLessonCount, setBonusLessonCount] = useState(1);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [badgeUser, setBadgeUser] = useState(null);

  const [refreshKey, setRefreshKey] = useState(0);
  // Fetch riders
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        role: '0', // Rider role (0) - API expects number as string
        _t: Date.now(), // Cache busting parameter
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(createApiUrlWithPrefix(`users?${params}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const riders = response.data.users || response.data;

      // Debug: Log image paths for riders (for comparison)
      console.log('🖼️ Riders data received:', riders.length, 'riders');
      riders.slice(0, 2).forEach((rider, index) => {
        console.log(`🖼️ Rider ${index + 1}:`, {
          id: rider.id,
          name: rider.name,
          photoPath: rider.photoPath,
          PhotoPath: rider.PhotoPath,
          imagePath: rider.imagePath,
          ImagePath: rider.ImagePath,
          role: rider.role
        });
      });

      setUsers(riders);
      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalItems || response.data.length);
    } catch (error) {
      console.error('Failed to fetch riders:', error);
      toast.error(t('common.failedToLoadRiders'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(null);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Listen for the custom event from sidebar
  useEffect(() => {
    const handleOpenAddUserModal = (event) => {
      if (event.detail.role === 'Rider') {
        setShowModal(true);
      }
    };

    window.addEventListener('openAddUserModal', handleOpenAddUserModal);
    return () => {
      window.removeEventListener('openAddUserModal', handleOpenAddUserModal);
    };
  }, []);

  // Event handlers
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  const handleUserCreated = async () => {
    console.log('🔄 User created, refreshing list...');
    await fetchUsers(); // Refresh the list
    setRefreshKey(prev => prev + 1); // Force component refresh
    console.log('✅ Users list refreshed after creation');

    // Refresh sidebar counts
    refreshUserCounts();
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleViewUserDetails = (user) => {
    setViewingUser(user);
    setShowDetailsModal(true);
  };

  const handleViewRiderStatus = async (user) => {
    setStatusUser(user);
    setShowStatusModal(true);
    setDropdownOpen(null);
    setRiderStatus({ insurance: null, forfait: null, forfaits: [], loading: true });

    try {
      // Fetch insurance status
      const insuranceResponse = await axios.get(
        createApiUrlWithPrefix(`RiderPayment/rider/${user.id}/insurance`),
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      ).catch((error) => {
        console.error('Error fetching insurance:', error.response?.status, error.response?.data);
        return null;
      });

      // Fetch forfait status
      const forfaitResponse = await axios.get(
        createApiUrlWithPrefix(`RiderPayment/rider/${user.id}/forfait`),
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      ).catch((error) => {
        console.error('Error fetching forfait:', error.response?.status, error.response?.data);
        return null;
      });

      // Fetch all forfaits
      const token = localStorage.getItem('token');
      console.log('Fetching forfaits for rider:', user.id, 'with token:', token ? 'Present' : 'Missing');

      const forfaitsResponse = await axios.get(
        createApiUrlWithPrefix(`RiderPayment/rider/${user.id}/forfaits`),
        { headers: { 'Authorization': `Bearer ${token}` } }
      ).catch((error) => {
        console.error('Error fetching forfaits:', error.response?.status, error.response?.data);
        return null;
      });

      console.log('API Responses:', {
        insurance: insuranceResponse?.data,
        forfait: forfaitResponse?.data,
        forfaits: forfaitsResponse?.data
      });

      setRiderStatus({
        insurance: insuranceResponse?.data || null,
        forfait: forfaitResponse?.data || null,
        forfaits: forfaitsResponse?.data || [],
        loading: false
      });
    } catch (error) {
      console.error('Error fetching rider status:', error);
      setRiderStatus({ insurance: null, forfait: null, forfaits: [], loading: false });
      toast.error(t('common.failedToFetchRiderStatus'));
    }
  };

  const handleGiveBonusLessons = (user) => {
    console.log('🎯 Setting bonus user:', { user, id: user?.id, Id: user?.Id });
    // Ensure we have a valid user with an id
    if (!user || (!user.id && !user.Id)) {
      console.error('❌ Invalid user object for bonus lessons:', user);
      toast.error('Invalid rider selected');
      return;
    }
    setBonusUser(user);
    setBonusLessonCount(1);
    setShowBonusModal(true);
    setDropdownOpen(null);
  };

  const handleDropdownToggle = (userId) => {
    setDropdownOpen(dropdownOpen === userId ? null : userId);
  };

  const handleAction = (action, user) => {
    setDropdownOpen(null);
    switch (action) {
      case 'view':
        handleViewUserDetails(user);
        break;
      case 'status':
        handleViewRiderStatus(user);
        break;
      case 'edit':
        handleEditUser(user);
        break;
      case 'delete':
        handleDeleteUser(user.id);
        break;
      case 'bonus':
        handleGiveBonusLessons(user);
        break;
      case 'print':
        setBadgeUser(user);
        setShowBadgeModal(true);
        break;
      case 'payments':
        window.open('/payments/riders', '_blank');
        break;
      default:
        break;
    }
  };

  const handleSubmitBonusLessons = async (e) => {
    e.preventDefault();

    if (!bonusUser) {
      toast.error(t('common.noRiderSelected'));
      return;
    }

    if (bonusLessonCount <= 0) {
      toast.error(t('common.lessonCountMustBeGreaterThanZero'));
      return;
    }

    try {
      // Validate bonusUser has an id (check both camelCase and PascalCase)
      const riderId = bonusUser?.id || bonusUser?.Id;
      if (!bonusUser || !riderId) {
        console.error('❌ Invalid bonusUser:', bonusUser);
        toast.error('Invalid rider selected for bonus lessons');
        return;
      }

      const url = createApiUrlWithPrefix(`RiderPayment/rider/${riderId}/bonus-lessons`);
      console.log('🎯 Bonus Lessons API Call:', {
        url,
        bonusUser,
        riderId,
        lessonCount: bonusLessonCount,
        processedBy: parseInt(localStorage.getItem('userId') || '1')
      });

      const response = await axios.post(
        url,
        {
          lessonCount: bonusLessonCount,
          processedBy: parseInt(localStorage.getItem('userId') || '1')
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Bonus lessons given successfully:', response.data);
      toast.success(`${bonusLessonCount} bonus lesson(s) given to ${bonusUser.name || bonusUser.firstName} ${bonusUser.lastName || ''}`);

      // Close modal and reset
      setShowBonusModal(false);
      setBonusUser(null);
      setBonusLessonCount(1);

    } catch (error) {
      console.error('❌ Error giving bonus lessons:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });

      // Handle structured error responses from backend
      let errorMessage = t('common.failedToGiveBonusLessons');

      if (error.response?.data) {
        const errorData = error.response.data;

        // Handle string response
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
        // Handle object with message property
        else if (errorData.message && typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
        // Handle validation errors object
        else if (errorData.errors && typeof errorData.errors === 'object') {
          const errors = errorData.errors;
          if (Array.isArray(errors)) {
            errorMessage = errors.filter(e => typeof e === 'string').join(', ');
          } else {
            errorMessage = Object.values(errors)
              .flat()
              .filter(e => typeof e === 'string')
              .join(', ');
          }
        }
        // Handle ASP.NET Core validation problem details
        else if (errorData.title && typeof errorData.title === 'string') {
          errorMessage = errorData.title;
        }
        // Handle detail property
        else if (errorData.detail && typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // Fallback: convert object to JSON string for debugging
        else {
          console.error('Unhandled error format:', errorData);
          errorMessage = `Error: ${error.response.status} ${error.response.statusText || 'Bad Request'}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Ensure errorMessage is always a string (never an object)
      if (typeof errorMessage !== 'string') {
        errorMessage = String(errorMessage);
      }

      toast.error(errorMessage);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await axios.delete(createApiUrlWithPrefix(`users/${userId}`), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        toast.success(t('common.userDeletedSuccessfully'));

        // Check if we need to go back a page after deletion
        const currentUsersCount = users.length;
        if (currentUsersCount === 1 && currentPage > 1) {
          const newPage = Math.max(currentPage - 1, 1);
          setCurrentPage(newPage);
          // Don't call fetchUsers here - let useEffect handle it when currentPage changes
        } else {
          // Only refresh immediately if we're not changing pages
          await fetchUsers();
        }

        // Force component refresh
        setRefreshKey(prev => prev + 1);

        // Refresh sidebar counts
        refreshUserCounts();
      } catch (error) {
        console.error('Failed to delete user:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);

        // Extract error message from various possible locations
        let errorMessage = t('common.failedToDeleteUser');

        if (error.response?.data) {
          const errorData = error.response.data;
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        // Check for specific error types
        if (error.response?.status === 403) {
          toast.error('Access denied: You do not have permission to delete users. Admin or SuperAdmin role required.');
        } else if (error.response?.status === 401) {
          toast.error('Unauthorized: Please log in again.');
        } else if (error.response?.status === 404) {
          toast.error('User not found.');
        } else {
          toast.error(errorMessage);
        }
      }
    }
  };

  const handleUserUpdated = () => {
    setShowEditModal(false);
    setEditingUser(null);
    fetchUsers();
    // Refresh sidebar counts (in case role changed)
    refreshUserCounts();
  };

  // Print badge function
  const handlePrintBadge = async (user) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`users/${user.id}/qr-code`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const qrData = response.data.userGuid || response.data.qrCode || `EC_${user.id}`;

      // Debug logging
      console.log('User data for badge:', {
        firstName: user.firstName,
        lastName: user.lastName,
        birthDate: user.birthDate,
        bloodType: user.bloodType,
        qrData: qrData
      });

      // Calculate age from birth date (commented out as not used)
      // const age = user.birthDate && user.birthDate !== '0001-01-01T00:00:00' 
      //   ? new Date().getFullYear() - new Date(user.birthDate).getFullYear()
      //   : 'N/A';

      // Format birth date
      const birthDate = user.birthDate && user.birthDate !== null
        ? new Date(user.birthDate).toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
        : 'Not provided';

      // Create badge HTML for printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Badge - ${user.firstName} ${user.lastName}</title>
            <style>
              body { 
                font-family: 'Segoe UI', Arial, sans-serif; 
                margin: 0; 
                padding: 20px; 
                background: white;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
              }
              .badge { 
                width: 500px; 
                height: 420px; 
                border: 2px solid #2c3e50; 
                margin: 0 auto;
                background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                border-radius: 15px;
                box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                position: relative;
                overflow: hidden;
              }
              .header { 
                background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                color: white;
                padding: 15px 25px;
                text-align: center;
                font-weight: bold;
                font-size: 18px;
                letter-spacing: 1px;
              }
              .content { 
                padding: 25px;
                display: flex;
                gap: 20px;
                height: calc(100% - 70px);
              }
              .left-section { 
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 10px;
              }
              .user-photo { 
                width: 100px; 
                height: 100px; 
                border-radius: 50%; 
                border: 3px solid #3b82f6;
                object-fit: cover;
                margin-bottom: 15px;
              }
              .user-photo-placeholder { 
                width: 100px; 
                height: 100px; 
                border-radius: 50%; 
                border: 3px solid #3b82f6;
                background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: 32px;
                color: #3b82f6;
                margin-bottom: 15px;
              }
              .user-name { 
                font-size: 22px; 
                font-weight: bold; 
                color: #2c3e50;
                margin-bottom: 8px;
              }
              .user-details { 
                font-size: 14px; 
                color: #5a6c7d;
                line-height: 1.6;
              }
              .detail-item { 
                margin-bottom: 8px;
                display: flex;
                align-items: center;
                gap: 8px;
              }
              .right-section { 
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
              }
              .qr-container { 
                width: 140px; 
                height: 140px; 
                border: 2px solid #3b82f6;
                border-radius: 10px;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                background: white;
                margin-bottom: 15px;
              }
              .qr-code { 
                width: 120px;
                height: 120px;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .qr-label { 
                font-size: 12px; 
                color: #3b82f6;
                font-weight: bold;
                text-align: center;
                margin-bottom: 10px;
              }
              .role-badge { 
                background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                color: white;
                padding: 6px 16px;
                border-radius: 20px;
                font-size: 12px;
                font-weight: bold;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              @media print {
                body { margin: 0; padding: 10px; }
                .badge { margin: 0; box-shadow: none; }
              }
            </style>
          </head>
          <body>
            <div class="badge">
              <div class="header">EQUESTRIAN CLUB MEMBER</div>
              <div class="content">
                <div class="left-section">
                  ${(user.photoPath || user.imagePath) ?
          `<img src="${API_BASE_URL}/${user.photoPath || user.imagePath}" alt="User Photo" class="user-photo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />` :
          ''
        }
                  <div class="user-photo-placeholder" style="${(user.photoPath || user.imagePath) ? 'display: none;' : 'display: flex;'}">
                    ${user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  
                  <div class="user-name">${user.firstName} ${user.lastName}</div>
                  <div class="user-details">
                    <div class="detail-item">📅 Birth Date: ${birthDate}</div>
                    <div class="detail-item">🩸 Blood Type: ${user.bloodType || 'N/A'}</div>
                  </div>
                </div>
                
                <div class="right-section">
                  <div class="qr-container">
                    <div class="qr-code" id="qr-code-${user.id}"></div>
                  </div>
                  <div class="qr-label">SCAN QR CODE</div>
                  <div class="role-badge">RIDER</div>
                </div>
              </div>
            </div>
            <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js"></script>
            <script>
              // Generate QR code after page loads
              function generateQRCode() {
                const qrElement = document.getElementById('qr-code-${user.id}');
                console.log('QR Element found:', qrElement);
                console.log('QRCode library available:', typeof QRCode !== 'undefined');
                console.log('QR Data:', '${qrData}');
                
                if (qrElement && typeof QRCode !== 'undefined') {
                  try {
                    // Create a canvas element
                    const canvas = document.createElement('canvas');
                    qrElement.appendChild(canvas);
                    
                    QRCode.toCanvas(canvas, '${qrData}', {
                      width: 120,
                      height: 120,
                      margin: 1,
                      color: { 
                        dark: '#2c3e50', 
                        light: '#ffffff' 
                      },
                      errorCorrectionLevel: 'M'
                    }, function (error) {
                      if (error) {
                        console.error('QR Code generation failed:', error);
                        qrElement.innerHTML = '<div style="color: #2c3e50; font-size: 10px; text-align: center; padding: 10px; background: #f0f0f0; border: 1px solid #ccc; border-radius: 5px;">QR CODE<br/>${qrData}</div>';
                      } else {
                        console.log('QR Code generated successfully');
                        // Make sure the canvas is visible
                        canvas.style.display = 'block';
                        canvas.style.margin = '0 auto';
                      }
                    });
                  } catch (err) {
                    console.error('QR Code generation error:', err);
                    qrElement.innerHTML = '<div style="color: #2c3e50; font-size: 10px; text-align: center; padding: 10px; background: #f0f0f0; border: 1px solid #ccc; border-radius: 5px;">QR CODE<br/>${qrData}</div>';
                  }
                } else {
                  console.error('QR Element not found or QRCode library not loaded');
                  if (qrElement) {
                    qrElement.innerHTML = '<div style="color: #2c3e50; font-size: 10px; text-align: center; padding: 10px; background: #f0f0f0; border: 1px solid #ccc; border-radius: 5px;">QR CODE<br/>${qrData}</div>';
                  }
                }
              }
              
              // Try multiple times to ensure QRCode library is loaded
              let attempts = 0;
              const maxAttempts = 10;
              
              function tryGenerateQR() {
                if (typeof QRCode !== 'undefined') {
                  generateQRCode();
                } else if (attempts < maxAttempts) {
                  attempts++;
                  setTimeout(tryGenerateQR, 200);
                } else {
                  console.error('QRCode library failed to load after multiple attempts');
                  const qrElement = document.getElementById('qr-code-${user.id}');
                  if (qrElement) {
                    qrElement.innerHTML = '<div style="color: #2c3e50; font-size: 10px; text-align: center; padding: 10px; background: #f0f0f0; border: 1px solid #ccc; border-radius: 5px;">QR CODE<br/>${qrData}</div>';
                  }
                }
              }
              
              // Start trying to generate QR code
              tryGenerateQR();
            </script>
              </div>
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.print();

      toast.success(t('common.badgeOpenedForPrinting'));
    } catch (error) {
      console.error('Failed to print badge:', error);
      toast.error(t('common.failedToPrintBadge'));
    }
  };

  /* removed early loading return to prevent search bar unmounting 
if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  } 
*/

  return (
    <>
      <div className="flex-1 flex flex-col min-h-0 bg-neutral-50" dir={isRTL ? 'rtl' : 'ltr'} key={refreshKey}>
        <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 py-6">
          {/* Header - Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900">{t('riders.title')}</h1>
              <p className="mt-2 sm:mt-3 text-sm sm:text-base text-neutral-600">
                {t('riders.subtitle')}
              </p>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="bg-primary-600 text-white px-3 py-2 sm:px-4 rounded-lg hover:bg-primary-700 transition-colors flex items-center min-h-[44px] min-w-[44px] touch-manipulation text-sm sm:text-base"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">{t('riders.addRider')}</span>
            </button>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
            <div className="flex flex-col lg:flex-row gap-4 items-center">
              {/* Search Input */}
              <div className="flex-1">
                <div className="relative">
                  <Search className={`absolute top-1/2 transform -translate-y-1/2 h-6 w-6 text-neutral-400 ${isRTL ? 'right-4' : 'left-4'}`} />
                  <input
                    type="text"
                    placeholder={t('riders.searchPlaceholder')}
                    value={searchTerm}
                    onChange={handleSearch}
                    className={`w-full py-4 px-12 text-lg border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'}`}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={clearFilters}
                  className="px-6 py-3 text-neutral-600 border border-neutral-300 rounded-xl hover:bg-neutral-50 transition-colors font-medium"
                >
                  {t('common.clear')}
                </button>

                <button
                  onClick={fetchUsers}
                  className="px-6 py-3 text-neutral-600 border border-neutral-300 rounded-xl hover:bg-neutral-50 transition-colors"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* End of Fixed Header Section */}

        {/* Scrollable Cards Section */}
        <div className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6 lg:px-8 pb-32">
          {/* Select All and Riders Cards */}
          <div className="space-y-4">
            {/* Select All Checkbox */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === users.length && users.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-3"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {t('common.selectAll')} ({selectedUsers.length} {t('common.of')} {users.length} {t('common.selected')})
                  </span>
                </div>
                {selectedUsers.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      {selectedUsers.length} {t('common.riders')} {t('common.selected')}
                    </span>
                    <button className="px-3 py-1 text-sm text-error-600 hover:text-error-800 hover:bg-error-50 rounded-lg transition-colors">
                      {t('common.deleteSelected')}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Riders Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {users.map((user) => (
                <div key={user.id} className="relative bg-white rounded-lg shadow-md border border-neutral-200 hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  {/* Header with checkbox and role badge */}
                  <div className="flex items-center justify-between p-3 pb-2">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                      className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-accent-100 text-accent-800">
                      {t('common.riders')}
                    </span>
                  </div>

                  {/* User Info */}
                  <div className="px-3 pb-3">
                    {/* Avatar */}
                    <div className="flex justify-center mb-3">
                      {getUserImageUrl(user) ? (
                        <div className="relative">
                          <img
                            src={getUserImageUrl(user)}
                            alt={user.name || 'Rider'}
                            className="w-14 h-14 rounded-full object-cover border-2 border-yellow-100 shadow-sm"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-200 flex items-center justify-center border-2 border-yellow-100 shadow-sm hidden">
                            <span className="text-lg font-bold text-yellow-600">
                              {user.name?.charAt(0)?.toUpperCase() || 'R'}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-200 flex items-center justify-center border-2 border-yellow-100 shadow-sm">
                          <span className="text-lg font-bold text-yellow-600">
                            {user.name?.charAt(0)?.toUpperCase() || 'R'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Name and Email */}
                    <div className="text-center mb-3">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {user.name || t('common.unknownRider')}
                      </h3>
                      <p className="text-sm text-gray-500 truncate">{user.email}</p>
                    </div>

                    {/* Key Info */}
                    <div className="space-y-2 text-xs text-gray-600">
                      {user.phoneNumber && (
                        <div className="flex items-center justify-center">
                          <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-medium">
                            📞 {user.phoneNumber}
                          </span>
                        </div>
                      )}

                      {user.birthDate && user.birthDate !== '0001-01-01T00:00:00' && (
                        <div className="flex items-center justify-center">
                          <span className="bg-yellow-50 text-yellow-700 px-3 py-1 rounded-full font-medium">
                            🎂 {new Date().getFullYear() - new Date(user.birthDate).getFullYear()} years
                          </span>
                        </div>
                      )}

                      {user.level !== null && user.level !== undefined && (
                        <div className="flex items-center justify-center">
                          <span className="bg-yellow-50 text-yellow-700 px-3 py-1 rounded-full font-medium">
                            🏆 {user.level === 0 ? t('common.beginner') : user.level === 1 ? t('common.intermediate') : t('common.professional')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Dropdown - Top Right */}
                  <div className="absolute top-4 right-4 dropdown-container">
                    <button
                      onClick={() => handleDropdownToggle(user.id)}
                      className="flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Actions"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>

                    {dropdownOpen === user.id && (
                      <div className="absolute right-0 top-10 z-[9999] w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                        <button
                          onClick={() => handleAction('view', user)}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <Eye className="w-4 h-4 mr-3" />
                          {t('common.view')}
                        </button>
                        <button
                          onClick={() => handleAction('status', user)}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <CreditCard className="w-4 h-4 mr-3" />
                          {t('common.status')}
                        </button>
                        <button
                          onClick={() => handleAction('edit', user)}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <Edit className="w-4 h-4 mr-3" />
                          {t('common.edit')}
                        </button>
                        <button
                          onClick={() => handleAction('delete', user)}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4 mr-3" />
                          {t('common.delete')}
                        </button>
                        <div className="border-t border-gray-200 my-1"></div>
                        <button
                          onClick={() => handleAction('bonus', user)}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <BookOpen className="w-4 h-4 mr-3" />
                          {t('common.bonus')}
                        </button>
                        <button
                          onClick={() => handleAction('print', user)}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <Printer className="w-4 h-4 mr-3" />
                          {t('common.print')}
                        </button>
                        <button
                          onClick={() => handleAction('payments', user)}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <CreditCard className="w-4 h-4 mr-3" />
                          {t('common.payments')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* End of Scrollable Cards Section */}

        {/* Pagination - Sticky at bottom */}
        <div className="sticky bottom-0 bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 z-10 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <div className="text-xs sm:text-sm text-gray-700 font-medium">
              {t('common.showing')} <span className="font-bold text-blue-600">{((currentPage - 1) * itemsPerPage) + 1}</span> {t('common.to')} <span className="font-bold text-blue-600">{Math.min(currentPage * itemsPerPage, totalItems)}</span> {t('common.of')} <span className="font-bold text-blue-600">{totalItems}</span> {t('common.results')}
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 min-h-[44px] touch-manipulation"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">{t('common.previous')}</span>
                <span className="sm:hidden">Prev</span>
              </button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  if (pageNum > totalPages) return null;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 min-h-[44px] min-w-[44px] touch-manipulation ${currentPage === pageNum
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 min-h-[44px] touch-manipulation"
              >
                <span className="hidden sm:inline">{t('common.next')}</span>
                <span className="sm:hidden">Next</span>
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
        {/* End of Pagination */}

        {/* User Creation Modal */}
        <UserCreationModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          role="Rider"
          onUserCreated={handleUserCreated}
        />

        {/* User Edit Modal */}
        {editingUser && (
          <UserEditModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setEditingUser(null);
            }}
            user={editingUser}
            onUserUpdated={handleUserUpdated}
            disableRoleEdit={true}
          />
        )}

        {viewingUser && (
          <UserDetailsModal
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setViewingUser(null);
            }}
            user={viewingUser}
          />
        )}

        {/* Rider Status Modal */}
        {showStatusModal && statusUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold text-gray-900">
                    Rider Status - {statusUser.firstName} {statusUser.lastName}
                  </h2>
                  <button
                    onClick={() => {
                      setShowStatusModal(false);
                      setStatusUser(null);
                      setRiderStatus({ insurance: null, forfait: null, forfaits: [], loading: false });
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {riderStatus.loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Insurance Status */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <Shield className="w-6 h-6 text-blue-600 mr-3" />
                        <h3 className="text-lg font-semibold text-blue-900">{t('common.insurance')}</h3>
                      </div>
                      {riderStatus.insurance ? (
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">Status:</span>
                            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                              Active
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">Amount:</span>
                            <span className="text-sm text-gray-900">{riderStatus.insurance.amount.toLocaleString()} DA</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">Start Date:</span>
                            <span className="text-sm text-gray-900">
                              {new Date(riderStatus.insurance.startDate).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">Expiry Date:</span>
                            <span className="text-sm text-gray-900">
                              {new Date(riderStatus.insurance.expiryDate).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">Days Remaining:</span>
                            <span className="text-sm text-gray-900">
                              {Math.ceil((new Date(riderStatus.insurance.expiryDate) - new Date()) / (1000 * 60 * 60 * 24))} days
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-gray-500">{t('common.noActiveInsuranceFound')}</p>
                        </div>
                      )}
                    </div>

                    {/* Monthly Forfaits Status */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <Calendar className="w-6 h-6 text-green-600 mr-3" />
                        <h3 className="text-lg font-semibold text-green-900">{t('common.forfait')}</h3>
                      </div>
                      {riderStatus.forfaits && riderStatus.forfaits.length > 0 ? (
                        <div className="space-y-4">
                          {riderStatus.forfaits.map((forfait, index) => {
                            const isCurrentMonth = forfait.year === new Date().getFullYear() &&
                              forfait.month === new Date().getMonth() + 1;
                            const isExpired = new Date(forfait.expiryDate) < new Date();
                            const isFuture = forfait.year > new Date().getFullYear() ||
                              (forfait.year === new Date().getFullYear() && forfait.month > new Date().getMonth() + 1);

                            return (
                              <div key={forfait.id || index} className={`border rounded-lg p-4 ${isCurrentMonth ? 'bg-green-100 border-green-300' :
                                  isExpired ? 'bg-red-50 border-red-200' :
                                    isFuture ? 'bg-blue-50 border-blue-200' :
                                      'bg-gray-50 border-gray-200'
                                }`}>
                                <div className="flex justify-between items-start mb-3">
                                  <div>
                                    <h4 className="font-medium text-gray-900">
                                      {new Date(forfait.year, forfait.month - 1).toLocaleDateString('en-US', {
                                        month: 'long',
                                        year: 'numeric'
                                      })}
                                    </h4>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${isCurrentMonth ? 'bg-green-200 text-green-800' :
                                          isExpired ? 'bg-red-200 text-red-800' :
                                            isFuture ? 'bg-blue-200 text-blue-800' :
                                              'bg-gray-200 text-gray-800'
                                        }`}>
                                        {isCurrentMonth ? 'Current' :
                                          isExpired ? 'Expired' :
                                            isFuture ? 'Future' : 'Past'}
                                      </span>
                                      <span className="text-sm text-gray-600">
                                        {forfait.amount.toLocaleString()} DA
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  <div>
                                    <span className="text-gray-600">Total Lessons:</span>
                                    <span className="ml-2 font-medium">{forfait.totalLessons}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Used:</span>
                                    <span className="ml-2 font-medium">{forfait.usedLessons}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Remaining:</span>
                                    <span className="ml-2 font-medium">{forfait.remainingLessons}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Expires:</span>
                                    <span className="ml-2 font-medium">
                                      {new Date(forfait.expiryDate).toLocaleDateString()}
                                    </span>
                                  </div>
                                </div>

                                {forfait.extraLessonCount > 0 && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="flex items-center gap-2 text-sm">
                                      <BookOpen className="w-4 h-4 text-orange-600" />
                                      <span className="text-gray-600">Extra Lessons:</span>
                                      <span className="font-medium">{forfait.extraLessonCount}</span>
                                      {forfait.paidExtraLessonsAmount > 0 && (
                                        <span className="text-gray-500">({forfait.paidExtraLessonsAmount.toLocaleString()} DA)</span>
                                      )}
                                      {forfait.freeBonusLessonCount > 0 && (
                                        <span className="text-green-600 text-xs">({forfait.freeBonusLessonCount} free)</span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-gray-500">{t('common.noForfaitsFound')}</p>
                        </div>
                      )}
                    </div>

                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bonus Lessons Modal */}
        {showBonusModal && bonusUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Give Bonus Lessons
                  </h2>
                  <button
                    onClick={() => {
                      setShowBonusModal(false);
                      setBonusUser(null);
                      setBonusLessonCount(1);
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mb-6">
                  <div className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <BookOpen className="w-8 h-8 text-orange-600" />
                    <div>
                      <h3 className="font-medium text-orange-900">
                        {bonusUser.name || `${bonusUser.firstName} ${bonusUser.lastName}`}
                      </h3>
                      <p className="text-sm text-orange-700">
                        Give bonus lessons to this rider
                      </p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmitBonusLessons}>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Bonus Lessons
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={bonusLessonCount}
                      onChange={(e) => setBonusLessonCount(parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('common.enterNumberOfLessons')}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Bonus lessons are free (0 DA each)
                    </p>
                  </div>

                  <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <div className="w-5 h-5 text-yellow-600 mt-0.5">⚠️</div>
                      <div className="text-sm text-yellow-800">
                        <p className="font-medium mb-1">Important:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs">
                          <li>Rider must have an active monthly forfait</li>
                          <li>Bonus lessons will be added to the current month's forfait</li>
                          <li>Bonus lessons expire at the end of the current month</li>
                          <li>Total cost: 0 DA (Free bonus lessons)</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowBonusModal(false);
                        setBonusUser(null);
                        setBonusLessonCount(1);
                      }}
                      className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
                    >
                      Give {bonusLessonCount} Bonus Lesson{bonusLessonCount > 1 ? 's' : ''}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Badge Modal */}
        <UserBadgeModal
          isOpen={showBadgeModal}
          onClose={() => {
            setShowBadgeModal(false);
            setBadgeUser(null);
          }}
          user={badgeUser}
        />
      </div>
      {/* End of main flex container */}
    </>
  );
}

export default Riders;